'use client'

import { useCallback, useEffect, useState } from 'react'
import {
  rememberProfileMediaUrls,
  resolveNetworkProfileMediaUrl,
} from '@/lib/networkProfileMediaUrl'

export interface NetworkProfileMedia {
  tenantId: string
  avatarUrl?: string | null
  coverUrl?: string | null
  profileBio?: string | null
}

export async function fetchNetworkProfileMedia(tenantId: string): Promise<NetworkProfileMedia | null> {
  try {
    const res = await fetch(`/api/opas/pharmacy-network/profile/${encodeURIComponent(tenantId)}`, {
      credentials: 'include',
      cache: 'no-store',
    })
    const data = await res.json().catch(() => ({} as Record<string, unknown>))
    if (!res.ok) return null
    const avatarUrl = resolveNetworkProfileMediaUrl((data.avatarUrl as string | null | undefined) ?? null) ?? null
    const coverUrl = resolveNetworkProfileMediaUrl((data.coverUrl as string | null | undefined) ?? null) ?? null
    const profileBio = (data.profileBio as string | null | undefined) ?? null
    rememberProfileMediaUrls(tenantId, avatarUrl, coverUrl)
    return { tenantId, avatarUrl, coverUrl, profileBio }
  } catch {
    return null
  }
}

export function usePharmacyNetworkProfileMedia(
  tenantId: string | null,
  enabled: boolean,
  initial?: Pick<NetworkProfileMedia, 'avatarUrl' | 'coverUrl' | 'profileBio'> | null
) {
  const [media, setMedia] = useState<NetworkProfileMedia | null>(() => {
    if (!tenantId) return null
    if (initial?.avatarUrl != null || initial?.coverUrl != null || initial?.profileBio != null) {
      rememberProfileMediaUrls(tenantId, initial.avatarUrl ?? null, initial.coverUrl ?? null)
      return {
        tenantId,
        avatarUrl: initial.avatarUrl ?? null,
        coverUrl: initial.coverUrl ?? null,
        profileBio: initial.profileBio ?? null,
      }
    }
    return null
  })
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    if (!tenantId || !enabled) return null
    setLoading(true)
    try {
      const next = await fetchNetworkProfileMedia(tenantId)
      if (next) setMedia(next)
      return next
    } finally {
      setLoading(false)
    }
  }, [tenantId, enabled])

  useEffect(() => {
    if (!tenantId || !enabled) {
      setMedia(null)
      setLoading(false)
      return
    }
    if (initial?.avatarUrl != null || initial?.coverUrl != null || initial?.profileBio != null) {
      rememberProfileMediaUrls(tenantId, initial.avatarUrl ?? null, initial.coverUrl ?? null)
      setMedia({
        tenantId,
        avatarUrl: initial.avatarUrl ?? null,
        coverUrl: initial.coverUrl ?? null,
        profileBio: initial.profileBio ?? null,
      })
    }
    let cancelled = false
    setLoading(true)
    void fetchNetworkProfileMedia(tenantId).then((next) => {
      if (!cancelled && next) setMedia(next)
    }).finally(() => {
      if (!cancelled) setLoading(false)
    })
    return () => {
      cancelled = true
    }
  }, [tenantId, enabled, initial?.avatarUrl, initial?.coverUrl, initial?.profileBio])

  const updateMedia = useCallback(
    async (patch: {
      avatarDataUrl?: string | null
      coverDataUrl?: string | null
      clearAvatar?: boolean
      clearCover?: boolean
      profileBio?: string | null
    }) => {
      if (!tenantId) throw new Error('Eczane bulunamadı')
      setSaving(true)
      try {
        const body: Record<string, unknown> = {}
        if (patch.clearAvatar) body.clearAvatar = true
        else if (patch.avatarDataUrl != null) body.avatarDataUrl = patch.avatarDataUrl
        if (patch.clearCover) body.clearCover = true
        else if (patch.coverDataUrl != null) body.coverDataUrl = patch.coverDataUrl
        if (patch.profileBio != null) body.profileBio = patch.profileBio

        const res = await fetch('/api/opas/pharmacy-network/profile', {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        })
        const data = await res.json()
        if (!res.ok) throw new Error(data.error ?? 'Kaydedilemedi')
        const next: NetworkProfileMedia = {
          tenantId,
          avatarUrl: resolveNetworkProfileMediaUrl(data.avatarUrl ?? null) ?? null,
          coverUrl: resolveNetworkProfileMediaUrl(data.coverUrl ?? null) ?? null,
          profileBio: (data.profileBio as string | null | undefined) ?? null,
        }
        rememberProfileMediaUrls(tenantId, next.avatarUrl ?? null, next.coverUrl ?? null)
        setMedia(next)
        return next
      } finally {
        setSaving(false)
      }
    },
    [tenantId]
  )

  return { media, loading, saving, reload: load, updateMedia }
}
